import {
  Document,
  MediaProjectListItem,
  ProjectStatus,
  ProjectTag,
} from "pages/media/types";
import { calculateMediaAspectRatio } from "../../components/storyboardEditor/helpers";
import { isReservedProjectTag } from "./project-create-wizard";
import { CSSProperties } from "react";
import { CustomToken } from "theme";
import { GlobalToken, theme as antdTheme } from "antd";
import { OrganizationSubscription } from "types";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { ReservedProjectTags } from "../types";
import { InstanceStatus } from "../components/types";
import { radius } from "@libertify/ui";
dayjs.extend(isSameOrAfter);

export const sortedDocumentsPrimaryFirst = (
  primaryDocumentId: string | undefined,
  documents: Document[]
) => {
  return [...documents].sort((a, b) => {
    if (a.id === primaryDocumentId && b.id !== primaryDocumentId) return -1;
    if (b.id === primaryDocumentId && a.id !== primaryDocumentId) return 1;
    return new Date(a.created).getTime() - new Date(b.created).getTime();
  });
};

export const isPublicationAllDocumentsSelected = (
  publication: string[],
  documents: string[]
): boolean => {
  // If the project has more documents than the publication, it is not all selected
  if (publication.length !== documents.length) return false;

  const publicationSet = new Set(publication);
  return documents.every((doc) => publicationSet.has(doc));
};

export const isProjectHeyGenius = (type?: string | null) =>
  !!type?.includes("hey_genius");

export const isProjectFlash = (type: string) => {
  return type === "flash" || type === "flash_education";
};

export const isProjectFlashEducation = (type: string) => {
  return type === "flash_education";
};

export const getProjectDetailRoute = (
  projectId: string,
  type?: string,
  instanceId?: string
) => {
  if (isProjectFlashEducation(type ?? "")) {
    return `/media/projects/${projectId}/experience-v2${instanceId ? `/${instanceId}` : ""}`;
  }
  return `/media/projects/${projectId}`;
};

export const hasHeyGeniusDocTag = (tags?: ProjectTag[]) =>
  tags?.some((tag) => tag.tag_key === ReservedProjectTags.HEY_GENIUS_DOC) ??
  false;

export const hasPromptTag = (tags?: ProjectTag[]) =>
  tags?.some((tag) => tag.tag_key === ReservedProjectTags.PROMPT) ?? false;

// Project status flips to one of these as soon as a (re)generation is
// requested; the media's own status only flips once a worker actually picks
// it up, so both need checking to catch that gap (see MED-6969).
const GENERATING_PROJECT_STATUSES = new Set([
  "MediaGenerating",
  "MediaRequested",
  "MediaPending",
]);
const IN_FLIGHT_MEDIA_STATUSES = new Set(["Generating", "Pending"]);

// "Fresh media is on its way" — gates the republish prompt (see MED-7156).
export const computeIsMediaInFlight = (
  projectStatus?: string | null,
  mediaStatus?: string | null
) =>
  GENERATING_PROJECT_STATUSES.has(projectStatus ?? "") ||
  IN_FLIGHT_MEDIA_STATUSES.has(mediaStatus ?? "");

// "No video to show" — drives the generating spinners. The same, plus
// `Draft`: no media was ever requested, so there's nothing to show but
// nothing coming either. That one status is the whole difference.
export const computeIsMediaGenerating = (
  projectStatus?: string | null,
  mediaStatus?: string | null
) =>
  computeIsMediaInFlight(projectStatus, mediaStatus) || mediaStatus === "Draft";

// Storyboard is waiting for review — approving it (not just regenerating
// the media) is what's needed to clear it (see MED-6969).
export const isStoryboardAwaitingApproval = (
  storyboardStatus?: string | null
) => storyboardStatus === "Reviewing";

export const isTrialSubscription = (subscription: OrganizationSubscription) => {
  return (
    subscription?.subscribed_products?.[0]?.product_name === "Free Trial" ||
    subscription?.license_type === "Trial"
  );
};

export const isActiveSubscription = (expiryDate: string) => {
  return expiryDate
    ? dayjs(expiryDate).startOf("day").isSameOrAfter(dayjs().startOf("day"))
    : false;
};

export const getMediaAspectRatio = async (
  videoUrl: string
): Promise<{
  aspectRatio: number;
}> => {
  if (!videoUrl) {
    throw new Error("Video URL is required");
  }

  return new Promise((resolve, reject) => {
    const video = document.createElement("video");

    video.onloadedmetadata = () => {
      resolve({
        aspectRatio: calculateMediaAspectRatio(
          video.videoWidth / video.videoHeight
        ),
      });
    };

    video.onerror = () => {
      reject(new Error("Failed to load video metadata"));
    };

    video.src = videoUrl;
  });
};

export const filterOutReservedTags = (tags: ProjectTag[]) => {
  return tags?.filter((tag) => !isReservedProjectTag(tag.tag_key)) ?? [];
};

export const getFullTagList = (
  initialTags: ProjectTag[],
  userInputValues: string[]
) => {
  const reservedTags =
    initialTags?.filter((tag: ProjectTag) =>
      isReservedProjectTag(tag.tag_key)
    ) ?? [];

  const userTags = userInputValues.map((value) => {
    const tagValue = value.trim();
    return {
      tag_key: tagValue,
      tag_value: tagValue,
    };
  });

  return [...reservedTags, ...userTags];
};

export const getSharedPillStyles = (
  theme: GlobalToken & CustomToken,
  options: CSSProperties = {}
) => ({
  backgroundColor: options.color,
  border: `1px solid ${theme.colorGrey}`,
  borderRadius: radius.pill,
  padding: "4px 12px",
  fontSize: "12px",
  lineHeight: "25px",
  letterSpacing: "-.48px",
  fontWeight: 500,
  color: theme.colorTextBase,
  ...options,
});

export const sortTagsAlphabetically = (tags: ProjectTag[]) => {
  return [...tags].sort((a, b) => a.tag_value.localeCompare(b.tag_value));
};

const getStatusColor = (
  isLive: boolean,
  isReady: boolean,
  isErrored: boolean,
  isActionNeeded: boolean
) => {
  const { token } = antdTheme.useToken();
  const customToken = token as GlobalToken & CustomToken;
  if (isLive) return customToken.colorStatusLive;
  if (isReady) return customToken.colorStatusReady;
  if (isErrored) return customToken.colorStatusErrored;
  if (isActionNeeded) return customToken.colorStatusActionNeeded;
  return customToken.colorStatusInProgress;
};

export const getProjectStatusColor = (status: ProjectStatus) => {
  return getStatusColor(
    isProjectLive(status),
    isProjectReady(status),
    isProjectErrored(status),
    isProjectActionNeeded(status)
  );
};

export const isProjectLive = (status: ProjectStatus) => {
  return status === "Published";
};

export const isProjectReady = (status: ProjectStatus) => {
  return ["MediaGenerated", "Preview", "New"].includes(status);
};

export const isProjectErrored = (status: ProjectStatus) => {
  return status.includes("Failed");
};

export const isProjectActionNeeded = (status: ProjectStatus) => {
  return (["StoryboardReviewing", "WaitingForSettings"] as string[]).includes(
    status
  );
};

export const mapProjectStatus = (status: ProjectStatus) => {
  if (isProjectLive(status)) return "live";
  if (isProjectReady(status)) return "ready";
  if (isProjectErrored(status)) return "error";
  if (isProjectActionNeeded(status)) return "actionNeeded";
  return "inProgress";
};

export const getProjectStatusDotColor = (status: ProjectStatus) => {
  if (isProjectLive(status)) return "#289A48";
  if (isProjectReady(status)) return "#7acced";
  if (isProjectErrored(status)) return "#ff5a60";
  return "#FBBE0A";
};

const isInstanceReady = (status: InstanceStatus) => {
  return status === "Generated";
};

const isInstanceErrored = (status: InstanceStatus) => {
  return status === "Error";
};

// `isLive` comes from the publication status (MED-6885), not `status`
// (media/instance generation status) — publishing is no longer a media state.
export const getInstanceStatusColor = (
  status: InstanceStatus,
  isLive: boolean
) => {
  return getStatusColor(
    isLive,
    isInstanceReady(status),
    isInstanceErrored(status),
    false
  );
};

export const getRecentProjects = (
  projects: MediaProjectListItem[] | undefined
) => {
  if (!projects) return [];
  return [...projects]
    .sort((a, b) => {
      if (!a.created || !b.created) return 0;
      return new Date(b.created).getTime() - new Date(a.created).getTime();
    })
    .slice(0, 6);
};
